import express, { Request, Response, NextFunction } from 'express';
import { pino } from 'pino';
import type { 
  RegistryConfig, 
  ApiResponse,
  ServiceFilter,
  DeployedService 
} from './types.js';
import { RegistryDatabase } from './database/index.js';
import { ServiceManager } from './service-manager.js';
import { HealthChecker } from './health-checker.js';

export class ApiServer {
  private app: express.Application;
  private logger = pino({ name: 'api-server' });
  private db: RegistryDatabase;
  private serviceManager: ServiceManager;
  private healthChecker: HealthChecker;
  private config: RegistryConfig['api'];

  constructor(
    db: RegistryDatabase,
    serviceManager: ServiceManager,
    healthChecker: HealthChecker,
    config: RegistryConfig['api']
  ) {
    this.db = db;
    this.serviceManager = serviceManager;
    this.healthChecker = healthChecker;
    this.config = config;
    this.app = express();
    this.setupMiddleware();
    this.setupRoutes();
  }

  private setupMiddleware(): void {
    // JSON 解析
    this.app.use(express.json());

    // CORS
    this.app.use((req, res, next) => {
      res.header('Access-Control-Allow-Origin', '*');
      res.header('Access-Control-Allow-Methods', 'GET, POST, PUT, DELETE, OPTIONS');
      res.header('Access-Control-Allow-Headers', 'Content-Type, Authorization');
      
      if (req.method === 'OPTIONS') {
        return res.sendStatus(204);
      }
      next();
    });

    // 认证中间件
    if (this.config.auth?.enabled) {
      this.app.use((req, res, next) => {
        // 跳过健康检查端点
        if (req.path === '/health') {
          return next();
        }

        const authHeader = req.headers.authorization;
        if (!authHeader || !authHeader.startsWith('Bearer ')) {
          return this.sendError(res, 401, 'Unauthorized');
        }

        const token = authHeader.substring(7);
        if (token !== this.config.auth?.token) {
          return this.sendError(res, 401, 'Invalid token');
        }

        next();
      });
    }

    // 请求日志
    this.app.use((req, res, next) => {
      this.logger.info({ method: req.method, path: req.path }, 'API request');
      next();
    });

    // 错误处理
    this.app.use((err: Error, req: Request, res: Response, next: NextFunction) => {
      this.logger.error({ error: err.message }, 'API error');
      this.sendError(res, 500, err.message);
    });
  }

  private setupRoutes(): void {
    // 健康检查
    this.app.get('/health', (req, res) => {
      this.sendSuccess(res, { status: 'ok', uptime: process.uptime() });
    });

    // 获取统计信息
    this.app.get('/api/stats', (req, res) => {
      const stats = this.db.getStatistics();
      const healthStatus = this.healthChecker.getStatus();
      
      this.sendSuccess(res, {
        services: stats,
        healthChecker: healthStatus
      });
    });

    // 列出所有服务
    this.app.get('/api/services', (req, res) => {
      const filter: ServiceFilter = {};
      
      if (req.query.status) filter.status = req.query.status as any;
      if (req.query.type) filter.type = req.query.type as any;
      if (req.query.projectId) filter.projectId = req.query.projectId as string;
      if (req.query.serverId) filter.serverId = req.query.serverId as string;
      
      const services = this.db.listServices(filter);
      this.sendSuccess(res, services);
    });

    // 获取单个服务
    this.app.get('/api/services/:id', (req, res) => {
      const service = this.db.getService(req.params.id);
      if (!service) {
        return this.sendError(res, 404, 'Service not found');
      }
      
      this.sendSuccess(res, service);
    });

    // 注册新服务
    this.app.post('/api/services', (req, res) => {
      try {
        const service: DeployedService = req.body;
        this.db.registerService(service);
        
        // 如果服务正在运行，启动健康检查
        if (service.status === 'running' && service.healthCheck?.enabled) {
          this.healthChecker.startHealthCheck(service).catch(err => {
            this.logger.error({ error: err.message }, 'Failed to start health check');
          });
        }
        
        this.sendSuccess(res, service, 201);
      } catch (error: any) {
        this.sendError(res, 400, error.message);
      }
    });

    // 更新服务
    this.app.put('/api/services/:id', (req, res) => {
      const service = this.db.getService(req.params.id);
      if (!service) {
        return this.sendError(res, 404, 'Service not found');
      }
      
      try {
        const updatedService = { ...service, ...req.body, id: service.id };
        this.db.registerService(updatedService);
        this.sendSuccess(res, updatedService);
      } catch (error: any) {
        this.sendError(res, 400, error.message);
      }
    });

    // 删除服务
    this.app.delete('/api/services/:id', (req, res) => {
      const service = this.db.getService(req.params.id);
      if (!service) {
        return this.sendError(res, 404, 'Service not found');
      }
      
      this.healthChecker.stopHealthCheck(req.params.id);
      this.db.deleteService(req.params.id);
      this.sendSuccess(res, { message: 'Service deleted' });
    });

    // 服务操作
    this.app.post('/api/services/:id/start', async (req, res) => {
      try {
        const result = await this.serviceManager.startService(req.params.id);
        this.sendSuccess(res, result);
      } catch (error: any) {
        this.sendError(res, 400, error.message);
      }
    });

    this.app.post('/api/services/:id/stop', async (req, res) => {
      try {
        const result = await this.serviceManager.stopService(req.params.id);
        this.sendSuccess(res, result);
      } catch (error: any) {
        this.sendError(res, 400, error.message);
      }
    });

    this.app.post('/api/services/:id/restart', async (req, res) => {
      try {
        const result = await this.serviceManager.restartService(req.params.id);
        this.sendSuccess(res, result);
      } catch (error: any) {
        this.sendError(res, 400, error.message);
      }
    });

    this.app.post('/api/services/:id/destroy', async (req, res) => {
      const confirmDestroy = req.body.confirm === true;
      if (!confirmDestroy) {
        return this.sendError(res, 400, 'Destroy operation requires confirmation');
      }
      
      try {
        const result = await this.serviceManager.destroyService(req.params.id, true);
        this.sendSuccess(res, result);
      } catch (error: any) {
        this.sendError(res, 400, error.message);
      }
    });

    // 健康检查操作
    this.app.post('/api/services/:id/health-check', async (req, res) => {
      const service = this.db.getService(req.params.id);
      if (!service) {
        return this.sendError(res, 404, 'Service not found');
      }
      
      try {
        const result = await this.healthChecker.performHealthCheck(service);
        this.sendSuccess(res, result);
      } catch (error: any) {
        this.sendError(res, 400, error.message);
      }
    });

    // 获取服务事件
    this.app.get('/api/services/:id/events', (req, res) => {
      const limit = parseInt(req.query.limit as string) || 100;
      const events = this.db.getServiceEvents(req.params.id, limit);
      this.sendSuccess(res, events);
    });

    // 获取操作历史
    this.app.get('/api/services/:id/operations', (req, res) => {
      const limit = parseInt(req.query.limit as string) || 50;
      const operations = this.db.getOperationHistory(req.params.id, limit);
      this.sendSuccess(res, operations);
    });

    // 批量健康检查
    this.app.post('/api/health-check/all', async (req, res) => {
      try {
        const results = await this.healthChecker.checkAllServices();
        this.sendSuccess(res, results);
      } catch (error: any) {
        this.sendError(res, 400, error.message);
      }
    });
  }

  // 启动服务器
  async start(): Promise<void> {
    return new Promise((resolve) => {
      const server = this.app.listen(this.config.port, this.config.host, () => {
        this.logger.info(
          { host: this.config.host, port: this.config.port },
          'API server started'
        );
        resolve();
      });

      // 优雅关闭
      process.on('SIGTERM', () => {
        server.close(() => {
          this.logger.info('API server stopped');
        });
      });
    });
  }

  // 响应辅助方法
  private sendSuccess(res: Response, data: any, status = 200): void {
    const response: ApiResponse = {
      success: true,
      data,
      timestamp: new Date().toISOString()
    };
    res.status(status).json(response);
  }

  private sendError(res: Response, status: number, error: string): void {
    const response: ApiResponse = {
      success: false,
      error,
      timestamp: new Date().toISOString()
    };
    res.status(status).json(response);
  }

  // 获取 Express 应用实例（用于测试）
  getApp(): express.Application {
    return this.app;
  }
}